import { questionTypeForField } from "src/pages/questionnaires/fieldLibrary";

// Catalogue set-ups that have already gone wrong on real products, and the
// plain-English warning that would have caught each one. The question and
// document editors show these as you type; the list switches ask before
// turning on a setting that matches one.
//
// Each check returns { key, severity, short, text, fix? }; `short` is the
// few words the lists show in their "Check" column. "warning" means "this is
// probably a mistake", never "you can't save": staff may know better.

const has = (text, pattern) => pattern.test(String(text ?? ""));

const OPTIONAL_WORDS = /\((if (available|any|applicable)|optional)\)|\bif (available|any|applicable)\b|\boptional\b/i;
const PHONE_WORDS = /\b(phone|mobile|telephone|tel|whats ?app|fax)\b/i;
const EMAIL_WORDS = /\be-?mail\b/i;
const NUMBERISH_TEXT = /\b(passport|id|national|reference|postal|post|zip|iban|account|card)\s*(number|no\.?|code)\b/i;
const COUNTRY_OF = /\bcountry of (birth|residence|issue)\b|\bbirth ?place\b|\bplace of birth\b/i;
const PHOTO_WORDS = /\b(photo|photograph|picture|selfie|headshot|portrait)\b/i;

export const QUESTION_TYPES = [
  { value: "text", label: "Text", hint: "Any other typed answer: names, addresses, reference numbers." },
  { value: "date", label: "Date", hint: "Travellers pick it on a calendar or type it. You can limit how early or late it can be." },
  {
    value: "phone",
    label: "Phone number",
    hint: "A phone number with its country code. Travellers get a country code picker and a phone keypad.",
  },
  { value: "email", label: "Email", hint: "An email address, checked for a valid format." },
  { value: "list", label: "Dropdown (pick one)", hint: "The traveller picks one of the options you add." },
  { value: "check_box", label: "Checkboxes", hint: "The traveller ticks any of the options you add." },
  { value: "number", label: "Number", hint: "Counts and amounts only (nights, children). Drops a leading + or 0." },
  { value: "nationality", label: "Nationality", hint: "A list of nationalities (Lebanese, French...). Not for countries." },
];

// A type this screen doesn't know (added on the API later, say) shows as
// its stored name rather than breaking the row.
export const typeLabel = (value) => QUESTION_TYPES.find((type) => type.value === value)?.label ?? (value ? String(value) : "—");

export const typeTakesOptions = (type) => type === "list" || type === "check_box";

/**
 * Warnings for a question as it stands in the editor (or a list row).
 * `field` is the linked field-library entry, when there is one.
 */
export function questionPitfalls({ list_name: name, type, is_required: required, options = [] }, field) {
  const out = [];
  const fits = questionTypeForField(field);
  if (fits && type && fits !== type) {
    out.push({
      key: "field-type",
      severity: "warning",
      short: `Better as ${typeLabel(fits)}`,
      text: `It fills from "${field.name}", which holds ${FIELD_KIND[field.type] ?? "a value"}. A ${typeLabel(type)} question doesn't fit that; use ${typeLabel(fits)}.`,
      fix: { type: fits },
    });
  }
  if ((type === "text" || type === "number") && has(name, PHONE_WORDS) && !out.length) {
    out.push({
      short: "Better as a phone number",
      key: "phone-number",
      severity: "warning",
      text:
        type === "number"
          ? "This asks for a phone number. A Number field drops the + and any leading 0, so it can't be dialled; Phone number gives travellers a country code picker and a phone keypad."
          : "This asks for a phone number. Phone number gives travellers a country code picker and a phone keypad, so the number comes back complete and diallable.",
      fix: { type: "phone" },
    });
  } else if (type === "text" && has(name, EMAIL_WORDS) && !out.length) {
    out.push({
      short: "Better as an email",
      key: "email-text",
      severity: "warning",
      text: "This asks for an email address. Email checks the format, so a typo like name@gmail is caught before the form is sent.",
      fix: { type: "email" },
    });
  } else if (type === "number" && has(name, NUMBERISH_TEXT) && !out.length) {
    out.push({
      short: "Code kept as a number",
      key: "code-number",
      severity: "warning",
      text: "Reference numbers and codes should be Text: a Number field drops leading zeros and letters.",
      fix: { type: "text" },
    });
  }
  if (type === "nationality" && has(name, COUNTRY_OF) && !out.length) {
    out.push({
      short: "Country asked as a nationality",
      key: "country-as-nationality",
      severity: "warning",
      text: "This asks for a country, but the Nationality type lists nationalities (\"Lebanese\", not \"Lebanon\"). Use Text so travellers can write the country.",
      fix: { type: "text" },
    });
  }
  if (typeTakesOptions(type) && has(name, COUNTRY_OF) && options.length > 0) {
    out.push({
      key: "country-options",
      short: "Options may be nationalities",
      severity: "warning",
      text: "This asks for a country: check the options are countries (\"Lebanon\"), not nationalities (\"Lebanese\").",
    });
  }
  if (truthy(required) && has(name, OPTIONAL_WORDS)) {
    out.push({
      key: "optional-required",
      severity: "warning",
      short: "\"If available\" but required",
      text: "The wording says this may not apply (\"if available\"), but it is required, so travellers without one can't finish the form. Make it optional.",
      fix: { is_required: false },
    });
  }
  const trimmed = String(name ?? "").trim();
  if (/^[a-z]/.test(trimmed)) {
    const fixed = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    out.push({
      key: "lowercase",
      severity: "info",
      short: "Starts with a small letter",
      text: `Travellers see the question exactly as written: "${trimmed}". Start it with a capital letter ("${fixed}").`,
      fix: { list_name: fixed },
    });
  }
  if (!typeTakesOptions(type) && options.length > 0) {
    out.push({
      key: "stray-options",
      severity: "info",
      short: "Answer choices not used",
      text: `This question still has ${options.length} option${options.length === 1 ? "" : "s"} (${options
        .slice(0, 3)
        .map((option) => `"${option?.type ?? option}"`)
        .join(", ")}${options.length > 3 ? "…" : ""}) from an earlier type. Only List and Checkboxes questions use options.`,
    });
  }
  return out;
}

/** Warnings for a document as it stands in the editor (or a list row). */
export function documentPitfalls({
  documents_name: name,
  documents_description: description,
  is_required: required,
  required_validation: checksValidity,
  number_of_months: monthsValue,
}) {
  const out = [];
  if (truthy(checksValidity) && has(name, PHOTO_WORDS)) {
    out.push({
      key: "photo-validity",
      severity: "warning",
      short: "Validity rule on a photo",
      text: "Photos have no expiry date, so a \"valid for N months\" rule can't be met and may block travellers. Turn the validity check off for photos.",
      fix: { required_validation: false },
    });
  }
  if (truthy(checksValidity) && !(Number(monthsValue) > 0)) {
    out.push({
      key: "validity-zero",
      severity: "warning",
      short: "Validity of 0 months",
      text: "The validity check is on but the number of months is empty or 0, so it checks nothing.",
    });
  }
  if (truthy(required) && (has(name, OPTIONAL_WORDS) || has(stripTags(description), OPTIONAL_WORDS))) {
    out.push({
      key: "optional-required",
      severity: "warning",
      short: "\"If available\" but required",
      text: "The wording says this may not apply (\"if available\"), but it is required, so travellers without one can't finish. Make it optional.",
      fix: { is_required: false },
    });
  }
  return out;
}

const FIELD_KIND = {
  text: "text",
  date: "a date",
  select: "one of a fixed set of answers",
  boolean: "a yes or no answer",
  number: "a number",
  country: "a country",
  phone: "a phone number",
  email: "an email address",
};

function truthy(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

function stripTags(html) {
  return String(html ?? "").replace(/<[^>]*>/g, " ");
}
